'use client';

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

interface Member { name: string; role: string; quote: string; image: string; }
interface Props { members: Member[]; }

export default function MembersCarousel({ members }: Props) {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const [userIp, setUserIp] = useState<string>('0.0.0.0');
  const [loadTime] = useState(() => new Date().toISOString());
  const [secureImages, setSecureImages] = useState<Record<number, string>>({});
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const total = members.length;

  // Silent protection — no alerts, no messages
  useEffect(() => {
    // Fetch IP for dynamic watermarking
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setUserIp(data.ip))
      .catch(() => {});

    const noContext = (e: MouseEvent) => e.preventDefault();
    const noKeys = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && ['s','u','p','a'].includes(e.key.toLowerCase()))
        e.preventDefault();
    };
    document.addEventListener('contextmenu', noContext);
    document.addEventListener('keydown', noKeys);
    return () => {
      document.removeEventListener('contextmenu', noContext);
      document.removeEventListener('keydown', noKeys);
    };
  }, []);

  // Dynamic watermarking logic
  const protectImage = useCallback((idx: number) => {
    if (secureImages[idx]) return;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = members[idx].image;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.width = img.width;
      canvas.height = img.height;

      // Draw original image
      ctx.drawImage(img, 0, 0);

      // Add dynamic watermark
      ctx.save();
      // Extremely subtle text (0.015 opacity is almost invisible but detectable in pixel analysis)
      ctx.globalAlpha = 0.018;
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 10px Inter, sans-serif';
      
      const watermark = `${userIp} | ${loadTime} | MELON_PRIVATE_ID`;
      const step = 45;
      
      // Repeating vertical ring/scanner pattern
      for (let y = 0; y < canvas.height; y += step) {
        for (let x = 0; x < canvas.width; x += 140) {
          ctx.fillText(watermark, x, y);
        }
      }

      // Adversarial Noise Grid (High Frequency)
      // This creates subtle interference that AI enhancers struggle to 'smooth' out
      ctx.globalAlpha = 0.012;
      for (let i = 0; i < canvas.width; i += 4) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.strokeStyle = i % 8 === 0 ? '#000000' : '#ffffff';
        ctx.stroke();
      }
      
      // Add high-frequency noise "ring" at the borders
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.03;
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, Math.min(canvas.width, canvas.height) / 2 - 5, 0, Math.PI * 2);
      ctx.stroke();

      ctx.restore();

      setSecureImages(prev => ({ ...prev, [idx]: canvas.toDataURL('image/jpeg', 0.95) }));
    };
  }, [members, secureImages, userIp, loadTime]);

  // Pre-protect current and adjacent images
  useEffect(() => {
    protectImage(current);
    protectImage((current + 1) % total);
    protectImage((current - 1 + total) % total);
  }, [current, total, protectImage]);

  const goTo = useCallback((idx: number) => {
    if (fading) return;
    setFading(true);
    setTimeout(() => { setCurrent((idx + total) % total); setFading(false); }, 320);
  }, [fading, total]);

  // Auto-scroll every 2 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      if (!fading) {
        goTo(current + 1);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [current, fading, goTo]);

  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = null;
  };
  const onTouchMove = (e: React.TouchEvent) => { touchEndX.current = e.targetTouches[0].clientX; };
  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const d = touchStartX.current - touchEndX.current;
    if (Math.abs(d) > 48) { if (d > 0) next(); else prev(); }
  };

  const m = members[current];
  const showDots = total <= 15;

  return (
    <div className="mc-wrap">
      <div
        className="mc-card"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Avatar with animated gradient ring */}
        <div className="mc-avatar-ring-wrap">
          <div className="mc-avatar-ring" />
          <div className="mc-avatar img-shield-wrap">
            {/* Explicit shield overlay - prevents direct image interaction even if z-index is challenged */}
            <div className="img-shield" onContextMenu={(e) => e.preventDefault()} />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={secureImages[current] || m.image}
              alt=""
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onContextMenu={(e) => e.preventDefault()}
              className={`mc-img${fading ? ' mc-fade' : ''}`}
            />
          </div>
        </div>

        {/* Content */}
        <div className={`mc-content${fading ? ' mc-fade' : ''}`}>
          <p className="mc-quote">&#8220;{m.quote}&#8221;</p>
          <h2 className="mc-name">{m.name}</h2>
          <h3 className="mc-role">{m.role}</h3>
        </div>

        {/* Navigation */}
        <div className="mc-nav">
          <button onClick={prev} className="mc-btn" aria-label="previous">&#8249;</button>

          {showDots ? (
            <div className="mc-dots">
              {members.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`mc-dot${i === current ? ' active' : ''}`}
                  aria-label={`member ${i + 1}`}
                />
              ))}
            </div>
          ) : (
            <span className="mc-counter">{current + 1} <span className="mc-sep">/</span> {total}</span>
          )}

          <button onClick={next} className="mc-btn" aria-label="next">&#8250;</button>
        </div>
      </div>
    </div>
  );
}
